import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FaPlus, FaSave, FaTrash } from 'react-icons/fa';
import { commercialConfigAPI } from '../services/api';
import { useAppSettings } from '../context/AppSettingsContext';
import { MODULE_ACCESS_CHANGED } from '../utils/moduleAccess';

// Settings → App Configuration → Company & Units. Day-to-day site setup moved
// out of Commercial Setup: company details and labels, the training and
// hold-time gates, measurement units, and the Unit/Plant and Section masters.
// Each unit carries its own address, printed in report headers while that unit
// is selected. How many units/sections are allowed is still set by the limits
// in Commercial Setup (enforced by the server).
const splitCommaValues = (value) =>
  String(value || '')
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean);

const hint = { fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', marginTop: '-6px' };

const CompanyUnitsEditor = () => {
  const { refreshAppSettings } = useAppSettings();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [limits, setLimits] = useState({});
  const [form, setForm] = useState({
    profile: { company_name: '', company_address: '', line_label: 'Section', plant_label: 'Unit/Plant' },
    training: { enforcement_mode: 'off', lms_sync_enabled: false },
    samplingPlan: { hold_time_sequence_mode: 'warn' },
    unitInputs: { fill_weight_units: '', fill_volume_units: '', label_claim_weight_units: '', label_claim_volume_units: '' },
    lines: [],
    plants: []
  });

  const load = async () => {
    setLoading(true);
    try {
      const data = (await commercialConfigAPI.getSite())?.data?.data || {};
      setLimits(data.limits || {});
      setForm({
        profile: {
          company_name: data.profile?.company_name || '',
          company_address: data.profile?.company_address || '',
          line_label: data.profile?.line_label || 'Section',
          plant_label: data.profile?.plant_label || 'Unit/Plant'
        },
        training: {
          enforcement_mode: ['off', 'warn', 'block'].includes(data.training?.enforcement_mode) ? data.training.enforcement_mode : 'off',
          lms_sync_enabled: !!data.training?.lms_sync_enabled
        },
        samplingPlan: {
          hold_time_sequence_mode: ['off', 'warn', 'block'].includes(data.samplingPlan?.hold_time_sequence_mode) ? data.samplingPlan.hold_time_sequence_mode : 'warn'
        },
        unitInputs: {
          fill_weight_units: (data.units?.fill_weight_units || ['mg', 'g', 'kg']).join(', '),
          fill_volume_units: (data.units?.fill_volume_units || ['mL', 'L']).join(', '),
          label_claim_weight_units: (data.units?.label_claim_weight_units || ['mg/Unit', 'g/Unit', 'mcg/Unit', 'IU/Unit', 'MIU/Unit', '%']).join(', '),
          label_claim_volume_units: (data.units?.label_claim_volume_units || ['mg/mL', 'mcg/mL', 'IU/mL', 'MIU/mL', '%']).join(', ')
        },
        lines: (data.lines || []).map((l) => ({
          line_code: l.line_code,
          line_name: l.line_name,
          plant_code: l.plant_code || '',
          route_of_admin: l.route_of_admin || 'oral'
        })),
        plants: (data.plants || []).map((row) => ({ plant_code: row.plant_code || '', plant_name: row.plant_name || '', address: row.address || '' }))
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load company & unit configuration');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const updateNested = (section, key, value) => {
    setForm((prev) => ({ ...prev, [section]: { ...prev[section], [key]: value } }));
  };
  const updateRow = (list, index, key, value) => {
    setForm((prev) => {
      const next = [...prev[list]];
      next[index] = { ...next[index], [key]: value };
      return { ...prev, [list]: next };
    });
  };
  const addRow = (list, row) => setForm((prev) => ({ ...prev, [list]: [...prev[list], row] }));
  const removeRow = (list, index) => setForm((prev) => ({ ...prev, [list]: prev[list].filter((_, i) => i !== index) }));

  const handleSave = async () => {
    const plants = form.plants
      .filter((row) => row.plant_name.trim())
      .map((row, index) => ({
        plant_code: (row.plant_code.trim() || `PLANT${index + 1}`).toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, ''),
        plant_name: row.plant_name.trim(),
        address: row.address.trim()
      }));
    if (plants.length === 0) {
      toast.warning('At least one Unit/Plant must be configured');
      return;
    }

    setSaving(true);
    try {
      await commercialConfigAPI.saveSite({
        profile: form.profile,
        training: form.training,
        samplingPlan: form.samplingPlan,
        units: {
          fill_weight_units: splitCommaValues(form.unitInputs.fill_weight_units),
          fill_volume_units: splitCommaValues(form.unitInputs.fill_volume_units),
          label_claim_weight_units: splitCommaValues(form.unitInputs.label_claim_weight_units),
          label_claim_volume_units: splitCommaValues(form.unitInputs.label_claim_volume_units)
        },
        lines: form.lines.filter((l) => l.line_name.trim()),
        plants
      });
      await refreshAppSettings();
      window.dispatchEvent(new Event(MODULE_ACCESS_CHANGED));
      toast.success('Company & unit configuration saved');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save company & unit configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading-container"><div className="spinner"></div></div>;

  const availablePlants = form.plants
    .filter((row) => row.plant_name.trim())
    .map((row) => ({ code: row.plant_code || row.plant_name, name: row.plant_name }));
  const limitNote = (max, count) => (max === null || max === undefined ? `${count} configured` : `${count} of ${max} allowed by the licensed plan`);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
        <button className="btn btn-primary" type="button" onClick={handleSave} disabled={saving}>
          <FaSave /> {saving ? 'Saving...' : 'Save Company & Units'}
        </button>
      </div>

      <div className="form-section">
        <h3>Company Profile</h3>
        <p style={hint}>The company address is the default for report headers; a unit's own address (below) is used instead while that unit is selected.</p>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Company Name</label>
            <input className="form-control" value={form.profile.company_name} onChange={(e) => updateNested('profile', 'company_name', e.target.value)} />
          </div>
          <div className="form-group" style={{ gridColumn: 'span 2' }}>
            <label className="form-label">Company Address</label>
            <textarea className="form-control" rows="2" value={form.profile.company_address} onChange={(e) => updateNested('profile', 'company_address', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Section Label</label>
            <input className="form-control" value={form.profile.line_label} onChange={(e) => updateNested('profile', 'line_label', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Plant Label</label>
            <input className="form-control" value={form.profile.plant_label} onChange={(e) => updateNested('profile', 'plant_label', e.target.value)} />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Training Compliance</h3>
        <p style={hint}>
          Controls whether cleaning, sampling, visual inspection and testing sign-offs require the signer to have current mandatory training.
          AiventraLMS training sync is {form.training.lms_sync_enabled ? 'active' : 'off'} (Integration Hub).
        </p>
        <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
          <div className="form-group">
            <label className="form-label">Mandatory Training Enforcement</label>
            <select className="form-control" value={form.training.enforcement_mode} onChange={(e) => updateNested('training', 'enforcement_mode', e.target.value)}>
              <option value="off">Off — no check performed</option>
              <option value="warn">Warn — allow sign-off, flag missing training</option>
              <option value="block">Block — sign-off requires current training</option>
            </select>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Hold-Time Sequence Gate</h3>
        <p style={hint}>
          Controls whether the DEHT (dirty hold time) → Cleaning Validation → CEHT (clean hold time) order is just a warning, or blocks final QA approval of a plan whose linked predecessor study isn't Approved yet.
        </p>
        <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
          <div className="form-group">
            <label className="form-label">Sequence Enforcement</label>
            <select className="form-control" value={form.samplingPlan.hold_time_sequence_mode} onChange={(e) => updateNested('samplingPlan', 'hold_time_sequence_mode', e.target.value)}>
              <option value="off">Off — no check performed</option>
              <option value="warn">Warn — allow QA approval, flag out-of-order sequence</option>
              <option value="block">Block — QA approval requires predecessor plan Approved</option>
            </select>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Unit Configuration</h3>
        <div className="form-grid">
          <div className="form-group"><label className="form-label">Fill Weight Units</label><input placeholder="e.g. mg, g, kg" className="form-control" value={form.unitInputs.fill_weight_units} onChange={(e) => updateNested('unitInputs', 'fill_weight_units', e.target.value)} /></div>
          <div className="form-group"><label className="form-label">Fill Volume Units</label><input placeholder="e.g. mL, L" className="form-control" value={form.unitInputs.fill_volume_units} onChange={(e) => updateNested('unitInputs', 'fill_volume_units', e.target.value)} /></div>
          <div className="form-group"><label className="form-label">Label Claim Weight Units</label><input placeholder="e.g. mg/Unit, %, ..." className="form-control" value={form.unitInputs.label_claim_weight_units} onChange={(e) => updateNested('unitInputs', 'label_claim_weight_units', e.target.value)} /></div>
          <div className="form-group"><label className="form-label">Label Claim Volume Units</label><input placeholder="e.g. mg/mL, %, ..." className="form-control" value={form.unitInputs.label_claim_volume_units} onChange={(e) => updateNested('unitInputs', 'label_claim_volume_units', e.target.value)} /></div>
        </div>
      </div>

      <div className="form-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>Unit / Plant Master *</h3>
          <button className="btn btn-sm btn-outline" type="button" onClick={() => addRow('plants', { plant_code: '', plant_name: '', address: '' })}><FaPlus /> Add Unit / Plant</button>
        </div>
        <p style={hint}>
          Every Unit/Plant saved here appears in the "Unit / Plant" selector at the top of every page. Its address is printed in report headers while that unit is selected (blank = company address). {limitNote(limits.max_plants, form.plants.length)}.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '1rem' }}>
          {form.plants.length === 0 && (
            <div style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>No units yet — click "Add Unit / Plant" to create one.</div>
          )}
          {form.plants.map((plant, index) => (
            <div key={index} className="admin-grid-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr 2.5fr auto', gap: '10px', alignItems: 'start', background: 'var(--bg-color)', padding: '10px', borderRadius: '8px' }}>
              <input className="form-control" placeholder="Code (e.g. UNIT-02)" value={plant.plant_code} onChange={(e) => updateRow('plants', index, 'plant_code', e.target.value)} />
              <input className="form-control" placeholder="Name (e.g. Unit 02 / Beta Site)" value={plant.plant_name} onChange={(e) => updateRow('plants', index, 'plant_name', e.target.value)} />
              <textarea className="form-control" rows="2" placeholder="Unit address for report headers (blank = company address)" value={plant.address} onChange={(e) => updateRow('plants', index, 'address', e.target.value)} />
              <button type="button" className="btn btn-sm btn-danger" onClick={() => removeRow('plants', index)}><FaTrash /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="form-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>Section Master</h3>
          <button className="btn btn-sm btn-outline" type="button" onClick={() => addRow('lines', { line_code: '', line_name: '', plant_code: '', route_of_admin: 'oral' })}><FaPlus /> Add Section</button>
        </div>
        <p style={hint}>{limitNote(limits.max_lines, form.lines.length)}.</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '1rem' }}>
          {form.lines.map((line, index) => (
            <div key={index} className="admin-grid-row" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1.5fr 1.5fr auto', gap: '10px', alignItems: 'center', background: 'var(--bg-color)', padding: '10px', borderRadius: '8px' }}>
              <input className="form-control" placeholder="Code" value={line.line_code} onChange={(e) => updateRow('lines', index, 'line_code', e.target.value)} />
              <input className="form-control" placeholder="Name" value={line.line_name} onChange={(e) => updateRow('lines', index, 'line_name', e.target.value)} />
              <select className="form-control" value={line.plant_code} onChange={(e) => updateRow('lines', index, 'plant_code', e.target.value)}>
                <option value="">No Plant</option>
                {availablePlants.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
              </select>
              <select className="form-control" value={line.route_of_admin || 'oral'} onChange={(e) => updateRow('lines', index, 'route_of_admin', e.target.value)}>
                <option value="oral">Oral / Solid</option>
                <option value="topical">Topical / Dermal</option>
                <option value="injectable">Injectable / Parenteral</option>
                <option value="inhalation">Inhalation</option>
                <option value="ophthalmic">Ophthalmic</option>
                <option value="transdermal">Transdermal</option>
              </select>
              <button type="button" className="btn btn-sm btn-danger" onClick={() => removeRow('lines', index)}><FaTrash /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CompanyUnitsEditor;
